import { apiClient } from './api.client';
import { authService } from './auth.service';
import { API_CONFIG } from '../config/api.config';
import { 
  Camera, 
  CameraStream, 
  ApiResponse, 
  EdgeCameraPreviewResponse, 
  EdgeSyncResponse, 
  EdgeSyncMode,
  EdgeProvisionRequest,
  EdgeProvisionSimpleRequest,
  EdgeProvisionResponse
} from '../types';

export interface CameraFilters {
  page?: number;
  size?: number;
  sort?: string;
  filter?: string;
}

export interface CreateCameraData {
  camera_location: string;
  camera_company: string;
  camera_mac: string;
  camera_model: string;
  camera_ip: string;
  camera_port: number;
  camera_username: string;
  camera_password: string;
  location_id: number;
}

export interface UpdateCameraData extends Partial<CreateCameraData> {}

export interface StreamRequest {
  camera_id: number;
  stream_type: 'hls' | 'rtsp' | 'webrtc' | 'http' | 'onvif' | 'snapshot';
  stream_quality: 'main' | 'sub';
}

export interface CameraStatus {
  camera_id: number;
  status: boolean;
  last_seen: string;
  signal_strength: number;
  recording: boolean;
}

export interface CloudCameraTokens {
  access_token: string;
  app_token: string;
  stream_domain: string;
  area_domain: string;
  serial_no: string;
  channel_no: string;
  verification_code: string;
}

class CameraService {
  private cloudTokenRequests = new Map<string, Promise<CloudCameraTokens>>();
  /**
   * Get all cameras with optional filters
   */
  async getCameras(filters?: CameraFilters): Promise<ApiResponse<Camera[]>> {
    return apiClient.get<ApiResponse<Camera[]>>(
      API_CONFIG.ENDPOINTS.CAMERAS,
      filters
    );
  }

  /**
   * Get camera by ID
   */
  async getCameraById(id: number): Promise<Camera> {
    return apiClient.get<Camera>(`${API_CONFIG.ENDPOINTS.CAMERA}/${id}`);
  }

  /**
   * Get cameras by location
   */
  async getCamerasByLocation(locationId: number, pagination?: { page?: number; size?: number }): Promise<ApiResponse<Camera[]>> {
    return this.getCameras({
      filter: `location_id::${locationId}`,
      ...pagination,
    });
  }

  /**
   * Create new camera
   */
  async createCamera(data: CreateCameraData): Promise<Camera> {
    return apiClient.post<Camera>(API_CONFIG.ENDPOINTS.CAMERAS, data);
  }

  /**
   * Update camera
   */
  async updateCamera(id: number, data: UpdateCameraData): Promise<Camera> {
    return apiClient.put<Camera>(`${API_CONFIG.ENDPOINTS.CAMERA}/${id}`, data);
  }

  /**
   * Delete camera
   */
  async deleteCamera(id: number): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(`${API_CONFIG.ENDPOINTS.CAMERA}/${id}`);
  }

  /**
   * Search cameras
   */
  async searchCameras(searchTerm: string): Promise<ApiResponse<Camera[]>> {
    return this.getCameras({
      filter: `camera_location::like::${searchTerm}`,
    });
  }

  /**
   * Get camera stream
   */
  async getCameraStream(request: StreamRequest): Promise<CameraStream> {
    const response = await apiClient.post<CameraStream>(
      API_CONFIG.ENDPOINTS.CAMERA_STREAM,
      request
    );
    return response;
  }

  /**
   * Get all camera streams for a location (bulk API with JWT tokens)
   */
  async getBulkCameraStreams(locationId: number, streamType = 'hls', streamQuality = 'main'): Promise<CameraStream[]> {
    // Determine access type based on environment
    // Development (localhost): Use internal IPs (192.168.x.x)
    // Production (deployed): Use external DDNS (obxoffice.ddns.net)
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const accessType = isLocalhost ? 'internal' : 'external';
    
    // Backend expects: {location_id: number, access_type: "internal"|"external"|null}
    const response = await apiClient.post<CameraStream[]>(
      '/camera-security/cameras/stream-info',
      {
        location_id: locationId,
        access_type: accessType
      }
    );
    
    return response;
  }

  /**
   * Get camera snapshot (uses new fallback mechanism: ISAPI -> NVR -> MediaMTX)
   */
  getCameraSnapshotUrl(cameraId: number): string {
    return `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CAMERA}/${cameraId}/snapshot-live`;
  }

  /**
   * Get camera snapshot as blob (for capturing photos)
   * Works for both cloud cameras and NVR cameras
   */
  async getCameraSnapshot(cameraId: number, quality: 'low' | 'medium' | 'high' = 'high', signal?: AbortSignal): Promise<Response> {
    const token = authService.getToken();
    if (!token) {
      throw new Error('Not authenticated');
    }
    const response = await fetch(
      `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CAMERA}/${cameraId}/snapshot?quality=${quality}`,
      {
        signal,
        cache: 'no-store',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );
    return response;
  }

  /**
   * Get camera status
   */
  async getCameraStatus(cameraId: number): Promise<CameraStatus> {
    return apiClient.get<CameraStatus>(
      `${API_CONFIG.ENDPOINTS.CAMERA}/${cameraId}/status`
    );
  }

  /**
   * Record camera stream
   */
  async recordStream(cameraId: number, duration: number, format = 'mp4'): Promise<any> {
    return apiClient.post(
      `${API_CONFIG.ENDPOINTS.CAMERA}/${cameraId}/record`,
      { duration, format }
    );
  }

  /**
   * Stop camera recording
   */
  async stopRecording(cameraId: number): Promise<{ message: string; file_url: string }> {
    return apiClient.post<{ message: string; file_url: string }>(
      `${API_CONFIG.ENDPOINTS.CAMERA}/${cameraId}/stop-record`,
      {}
    );
  }

  /**
   * Get camera playback
   */
  async getCameraPlayback(
    cameraId: number,
    startTime: string,
    endTime: string,
    page = 1,
    size = 20
  ): Promise<ApiResponse<any[]>> {
    return apiClient.get<ApiResponse<any[]>>(
      `${API_CONFIG.ENDPOINTS.CAMERA}/${cameraId}/playback`,
      { start_time: startTime, end_time: endTime, page, size }
    );
  }

  /**
   * Preview cameras on edge device (before syncing)
   */
  async previewEdgeCameras(locationId: number): Promise<EdgeCameraPreviewResponse> {
    const response = await apiClient.get<EdgeCameraPreviewResponse>(
      `${API_CONFIG.ENDPOINTS.CAMERA_SECURITY}/location/${locationId}/edge/preview`
    );
    return response;
  }

  /**
   * Sync cameras from edge device to database
   */
  async syncEdgeCameras(
    locationId: number,
    mode: EdgeSyncMode = 'add_only',
    useExternal: boolean = false
  ): Promise<EdgeSyncResponse> {
    const params = new URLSearchParams();
    params.append('mode', mode);
    if (useExternal) {
      params.append('use_external', 'true');
    }
    const endpoint = `${API_CONFIG.ENDPOINTS.CAMERA_SECURITY}/location/${locationId}/edge/sync?${params.toString()}`;
    const response = await apiClient.post<EdgeSyncResponse>(endpoint, {});
    return response;
  }

  /**
   * Provision cameras from NVR via ISAPI (auto-discovery)
   */
  async provisionEdgeCameras(
    locationId: number,
    options: Omit<EdgeProvisionRequest, 'location_id'> = {}
  ): Promise<EdgeProvisionResponse> {
    const endpoint = `${API_CONFIG.ENDPOINTS.CAMERA_SECURITY}/location/${locationId}/edge/provision`;
    const payload: EdgeProvisionRequest = {
      location_id: locationId,
      include_substreams: true,
      force_tcp: true,
      use_external: false,
      ...options
    };
    const response = await apiClient.post<EdgeProvisionResponse>(endpoint, payload);
    return response;
  }

  /**
   * Provision cameras with manual channel list (no ISAPI)
   */
  async provisionEdgeCamerasSimple(
    locationId: number,
    channels: number[],
    options: Omit<EdgeProvisionSimpleRequest, 'location_id' | 'channels'> = {}
  ): Promise<EdgeProvisionResponse> {
    const endpoint = `${API_CONFIG.ENDPOINTS.CAMERA_SECURITY}/location/${locationId}/edge/provision/simple`;
    const payload: EdgeProvisionSimpleRequest = {
      location_id: locationId,
      channels,
      include_substreams: true,
      force_tcp: true,
      ...options
    };
    const response = await apiClient.post<EdgeProvisionResponse>(endpoint, payload);
    return response;
  }

  /**
   * Get cloud preview URL for cloud-managed cameras (HikVision/Dahua/Ubiquiti)
   */
  async getCloudPreview(cameraId: number, protocol: 1 | 2 | 3 = 2, quality: 1 | 2 = 1): Promise<{
    success: boolean;
    camera_id: number;
    camera_name: string;
    stream_url: string;
    stream_id?: string;
    expire_time?: number;
    protocol: string;
    quality: string;
    provider: string;
    resource_id: string;
    device_serial: string;
  }> {
    const response = await apiClient.get<{
      success: boolean;
      camera_id: number;
      camera_name: string;
      stream_url: string;
      stream_id?: string;
      expire_time?: number;
      protocol: string;
      quality: string;
      provider: string;
      resource_id: string;
      device_serial: string;
    }>(
      `${API_CONFIG.ENDPOINTS.CAMERA}/${cameraId}/cloud-preview?protocol=${protocol}&quality=${quality}`
    );
    return response;
  }

  getCloudTokens(cameraId: number): Promise<CloudCameraTokens> {
    // Strict Mode and expanded/grid players can request the same camera at once.
    // Share only in-flight work, scoped to the signed-in session; do not cache tokens.
    const key = `${authService.getToken() || ''}:${cameraId}`;
    const pending = this.cloudTokenRequests.get(key);
    if (pending) return pending;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 25000);
    const request = apiClient.get<CloudCameraTokens>(
      `${API_CONFIG.ENDPOINTS.CAMERA}/${cameraId}/cloud-tokens`, undefined,
      { signal: controller.signal }
    ).catch(error => {
      if (controller.signal.aborted) throw new Error('Cloud authentication timed out. Retry this camera.');
      throw error;
    }).finally(() => {
      clearTimeout(timer);
      this.cloudTokenRequests.delete(key);
    });
    this.cloudTokenRequests.set(key, request);
    return request;
  }

  /**
   * Get cloud tokens for camera audio (two-way talk)
   * Returns format compatible with EZUITalk library
   */
  async getCameraCloudTokens(cameraId: number): Promise<{
    accessToken: string;
    apiDomain: string;
    deviceSerial: string;
    channelNo: string;
  }> {
    const tokens = await this.getCloudTokens(cameraId);
    return {
      accessToken: tokens.access_token,  // ✅ Use access_token for Talk API
      apiDomain: tokens.area_domain || 'https://isgpopen.ezvizlife.com',
      deviceSerial: tokens.serial_no,
      channelNo: tokens.channel_no || '1'
    };
  }

  /**
   * Update camera display order for live view grid
   * @param locationId - Location ID
   * @param cameraOrders - Array of camera IDs with their display orders
   */
  async updateCameraDisplayOrder(
    locationId: number,
    cameraOrders: Array<{ camera_id: number; display_order: number }>
  ): Promise<{ message: string; updated_count: number }> {
    return apiClient.put<{ message: string; updated_count: number }>(
      `${API_CONFIG.ENDPOINTS.CAMERAS}/display-order`,
      {
        location_id: locationId,
        camera_orders: cameraOrders,
      }
    );
  }
}

export const cameraService = new CameraService();
